import { CityMap } from './classes/map';
import { Sidebar } from './classes/sidebar';
import { Widget } from './classes/widget';
import { MarkerType } from '../interfaces';
import { Initializer } from './classes/initializer';
import { ActionButton } from './classes/action-button';

export function initMapApp() {
    const config = window.MapEngineConfig;
    if (!config) {
        console.error("MapEngineConfig not found!");
        return;
    }

    const map = new CityMap('map');
    const sidebar = new Sidebar('#sidebar');
    const widget = new Widget('#widget');
    const init = new Initializer();

    const hasGPX = init.gpx(map);
    const permalink = init.permalink(map, widget, sidebar);

    new ActionButton('help-button', () => {
        sidebar.fillWithHtmlFromUrl('/html-api/sidebar/help');
        window.history.pushState({ sb_out: 1 }, '', '/sidebar/help');
        if (!sidebar.isOpen()) sidebar.open();
    })

    new ActionButton('gps-button', (element?: HTMLElement) => {
        const status = map.toggleLocate();
        element?.classList.toggle('gps-active', status);
    })

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('drawer')) {
        widget.openDrawer();
    }

    const dodgeSidebar = (force: boolean | null = null) => {
        widget.dodgeSidebar(force || sidebar.isOpen());
        map.dodgeSidebar(force || sidebar.isOpen());
    }

    async function fetchData(callback?: () => void) {
        try {
            const response = await fetch('/backend-api/markers/init');
            if (!response.ok) {
                throw new Error('Failed to fetch data');
            }

            const jsonResponse = await response.json();

            const markersData = jsonResponse.markers;
            const markerTypes = jsonResponse.marker_types;

            markerTypes.map((markerType: MarkerType) => {
                map.addMarkerType(markerType.marker_type_name, markerType.icon);
            });

            if (markersData.length > 0) {
                let bounds = {
                    northEast: {
                        lat: markersData[0].lat,
                        lng: markersData[0].lng
                    },
                    southWest: {
                        lat: markersData[0].lat,
                        lng: markersData[0].lng
                    }
                };

                for (let i = 1; i < markersData.length; i++) {
                    const marker = markersData[i];
                    bounds.northEast.lat = Math.max(bounds.northEast.lat, marker.lat);
                    bounds.northEast.lng = Math.max(bounds.northEast.lng, marker.lng);
                    bounds.southWest.lat = Math.min(bounds.southWest.lat, marker.lat);
                    bounds.southWest.lng = Math.min(bounds.southWest.lng, marker.lng);
                }

                map.fitToBounds(bounds);
            }

            for (const markerData of markersData) {
                const { lat, lng } = markerData;

                const markerClick = () => {
                    let history_state = { sb_out: 1 };
                    const history = `/m/${markerData.id}`;
                    if (window.history.state !== null) {
                        window.history.replaceState(history_state, '', history)
                    } else {
                        window.history.pushState(history_state, '', history)
                    }
                    sidebar.fillWithHtmlFromUrl(`/html-api/markers/${markerData.id}/sidebar`);
                    sidebar.open();
                };

                const popupHtml = `
                    <div class="${markerData.image_url ? 'popup-content-image' : 'popup-content-text'}">
                        ${markerData.image_url ? `<img class="popup-image center" src="${markerData.image_url}" />` : ''}
                        <span class="popup-title">${markerData.popup_title || ''}</span>
                    </div>
                `;

                map.addMarker(lat, lng, {
                    id: markerData.id,
                    lat: markerData.lat,
                    lng: markerData.lng,
                    type: markerData.marker_type_name,
                    popupContent: popupHtml,
                    filterGroups: markerData.filter_groups,
                }, markerClick);
            }

            if (hasGPX) {
                map.showLayers([]);
            } else if (permalink.success) {
                 map.showLayers([
                    permalink.filterGroup
                ]);
                 widget.toggleFilterButton(permalink.filterGroup, true);
                
                if (permalink.fitBounds) {
                    map.fitBoundsToFilterGroup(permalink.filterGroup);
                }
            } else {
                map.showLayers(config.filterGroups);
            }
        } catch (error) {
            console.error(error);
        }
    }

    fetchData();

    sidebar.addEventListener('toggle', (event: CustomEvent) => {
        const isOpen = event.detail.open;
        const wasOpen = event.detail.wasOpen;
        if (wasOpen !== isOpen) {
            if (!isOpen) {
                window.history.replaceState({ sb_out: 1 }, '', '/');
            }
            dodgeSidebar(isOpen);
        }
    });

    window.addEventListener('popstate', function () {
        sidebar.close();
    });

    map.addClickHandler((event: L.LeafletMouseEvent) => {
        sidebar.close();
    });

    document.addEventListener('DOMContentLoaded', () => {
        if (sidebar.isOpen()) {
            sidebar.decryptParts();
            sidebar.addCloseHandlerToAll('.sidebar-nav');
            sidebar.initGallery();
            dodgeSidebar(true);

            window.history.pushState({ sb_out: 1 }, '', window.location.pathname);
        }

        init.widget(map, sidebar);
    });
}